<template>
  <div>
    <div v-if="loading" class="text-center py-6"><span class="spinner-border text-primary"></span></div>

    <template v-else>
      <div class="row g-3 mb-4">
        <div v-for="item in checklist" :key="item.vaccine" class="col-md-6">
          <div class="border rounded p-3 h-100 d-flex justify-content-between align-items-start gap-2">
            <div>
              <div class="fw-bolder fs-7">{{ item.label }}</div>
              <div class="text-muted fs-8">{{ item.period }}</div>
              <div class="text-muted fs-8" v-if="item.condition">{{ item.condition }}</div>
              <div class="fs-8 mt-1" v-if="item.doses_required > 1">Doses: {{ item.doses_applied }}/{{ item.doses_required }}</div>
              <div class="fs-8 mt-1" v-if="item.last_date">Última dose: {{ formatDateBr(item.last_date) }}</div>
            </div>
            <div class="text-end">
              <span class="badge" :class="checklistStatus(item.status, 'vaccine').badge">{{ checklistStatus(item.status, 'vaccine').label }}</span>
              <button v-if="!readonly && item.status !== 'done'" type="button" class="btn btn-sm btn-link p-0 d-block mt-2 fs-8 ms-auto"
                      @click="startCreate(item.vaccine)">
                Registrar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="fw-bolder fs-6">Registros</div>
        <button v-if="!readonly && !form" type="button" class="btn btn-sm btn-light-primary" @click="startCreate()">
          <i class="bi bi-plus-lg me-1"></i> Vacina
        </button>
      </div>

      <div v-if="form" class="card card-bordered mb-4">
        <div class="card-body p-4">
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label fs-7">Vacina</label>
              <select class="form-select form-select-sm" :class="{ 'is-invalid': errors.vaccine }" v-model="form.vaccine">
                <option v-for="option in vaccineOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
              <small class="text-danger" v-if="errors.vaccine">{{ errors.vaccine }}</small>
            </div>
            <div class="col-md-3">
              <label class="form-label fs-7">Situação</label>
              <select class="form-select form-select-sm" :class="{ 'is-invalid': errors.status }" v-model="form.status">
                <option v-for="option in statusOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
              <small class="text-danger" v-if="errors.status">{{ errors.status }}</small>
            </div>
            <div class="col-md-2">
              <label class="form-label fs-7">Dose</label>
              <input type="number" min="1" max="5" class="form-control form-control-sm" :class="{ 'is-invalid': errors.dose }" v-model="form.dose">
              <small class="text-danger" v-if="errors.dose">{{ errors.dose }}</small>
            </div>
            <div class="col-md-3">
              <label class="form-label fs-7">Data de aplicação</label>
              <input type="date" class="form-control form-control-sm" :class="{ 'is-invalid': errors.vaccination_date }" v-model="form.vaccination_date" :max="today">
              <small class="text-danger" v-if="errors.vaccination_date">{{ errors.vaccination_date }}</small>
            </div>
            <div class="col-md-3">
              <label class="form-label fs-7">Lote</label>
              <input type="text" class="form-control form-control-sm" :class="{ 'is-invalid': errors.lot }" v-model="form.lot">
            </div>
            <div class="col-md-9">
              <label class="form-label fs-7">Observações</label>
              <input type="text" class="form-control form-control-sm" v-model="form.notes">
            </div>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button type="button" class="btn btn-sm btn-light" @click="form = null">Cancelar</button>
            <button type="button" class="btn btn-sm btn-primary" :disabled="saving" @click="save">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span> Salvar
            </button>
          </div>
        </div>
      </div>

      <div v-if="!vaccinations.length" class="text-muted fs-7 text-center py-4">Nenhuma vacina registrada.</div>
      <div v-else class="table-responsive">
        <table class="table table-sm table-row-dashed align-middle fs-7">
          <thead>
          <tr class="text-muted fs-8 text-uppercase">
            <th>Vacina</th>
            <th>Situação</th>
            <th>Dose</th>
            <th>Data</th>
            <th>IG</th>
            <th>Lote</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="vaccination in vaccinations" :key="vaccination.id">
            <td class="fw-bold">{{ vaccination.vaccine_label }}</td>
            <td>{{ vaccination.status_label }}</td>
            <td>{{ vaccination.dose ?? '—' }}</td>
            <td>{{ formatDateBr(vaccination.vaccination_date) }}</td>
            <td>{{ vaccination.gestational_age?.label ?? (vaccination.vaccination_date ? 'antes da gestação' : '—') }}</td>
            <td>{{ vaccination.lot || '—' }}</td>
            <td class="text-end text-nowrap">
              <button v-if="!readonly" type="button" class="btn btn-sm btn-icon btn-light" title="Editar" @click="startEdit(vaccination)">
                <i class="bi bi-pencil"></i>
              </button>
              <button v-if="!readonly && canDelete" type="button" class="btn btn-sm btn-icon btn-light-danger ms-1" title="Excluir" @click="remove(vaccination)">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import http from '../../../../helpers/http';
import Alert from '../../../../helpers/alert';
import {
  VACCINATION_STATUS_OPTIONS,
  VACCINE_OPTIONS,
  buildPayload,
  checklistStatus,
  formatDateBr,
  todayIso,
  validationErrors,
} from '../prenatal';

export default {
  name: 'PrenatalVaccinesTab',
  emits: ['changed'],
  props: {
    baseUrl: { type: String, required: true },
    readonly: Boolean,
    canDelete: Boolean,
  },
  data() {
    return {
      loading: false,
      saving: false,
      vaccinations: [],
      checklist: [],
      form: null,
      editingId: null,
      errors: {},
      today: todayIso(),
      vaccineOptions: VACCINE_OPTIONS,
      statusOptions: VACCINATION_STATUS_OPTIONS,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    checklistStatus,
    formatDateBr,
    async load() {
      this.loading = true;
      try {
        const response = await http.get(`${this.baseUrl}/vaccinations`);
        this.vaccinations = response.data.vaccinations;
        this.checklist = response.data.checklist;
      } catch (_) {
        Alert.toastError('Falha ao carregar as vacinas.');
      } finally {
        this.loading = false;
      }
    },
    startCreate(vaccine = 'dtpa') {
      this.editingId = null;
      this.errors = {};
      this.form = { vaccine, status: 'applied', dose: '', vaccination_date: this.today, lot: '', notes: '' };
    },
    startEdit(vaccination) {
      this.editingId = vaccination.id;
      this.errors = {};
      this.form = {
        vaccine: vaccination.vaccine,
        status: vaccination.status,
        dose: vaccination.dose ?? '',
        vaccination_date: vaccination.vaccination_date ?? '',
        lot: vaccination.lot ?? '',
        notes: vaccination.notes ?? '',
      };
    },
    async save() {
      this.saving = true;
      this.errors = {};
      const payload = buildPayload(this.form, ['dose']);

      try {
        if (this.editingId) {
          await http.put(`${this.baseUrl}/vaccinations/${this.editingId}`, payload);
        } else {
          await http.post(`${this.baseUrl}/vaccinations`, payload);
        }
        this.form = null;
        Alert.toastSuccess('Vacina salva.');
        await this.load();
        this.$emit('changed');
      } catch (error) {
        this.errors = validationErrors(error);
        if (!Object.keys(this.errors).length) {
          Alert.toastError(error.response?.data?.message || 'Falha ao salvar a vacina.');
        }
      } finally {
        this.saving = false;
      }
    },
    async remove(vaccination) {
      const result = await Swal.fire({
        title: 'Excluir registro de vacina?',
        text: vaccination.vaccine_label,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary' },
      });
      if (!result.isConfirmed) {
        return;
      }

      try {
        await http.delete(`${this.baseUrl}/vaccinations/${vaccination.id}`);
        await this.load();
        this.$emit('changed');
      } catch (error) {
        Alert.toastError(error.response?.data?.message || 'Falha ao excluir a vacina.');
      }
    },
  },
};
</script>
